import { useLocation } from 'react-router-dom';
// eslint-disable-next-line max-len
import { UniversalProductsCategory } from '@1po/1po-bff-fe-spec/generated/catalog/universal_products/response/GetUniversalProductsTreeResponse';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import {
  CATALOG,
  ROUTER_CATALOG_DH,
  ROUTER_CATALOG_IAM,
  ROUTER_CATALOG_SEARCH,
  ROUTER_UNIVERSAL_PRODUCTS,
} from 'app/catalogRoutes';
import {
  CERTAS_OIL_VISCOSITY_REGEX,
  IAM,
  IamCatalogBrandsType,
  OEM_BRAND_KEY,
  OTHER_BRAND_KEY,
  SearchStatus,
  UNIVERSAL_PRODUCTS,
  UNIVERSAL_PRODUCTS_NODE_ID,
  UNIVERSAL_PRODUCTS_OILS_NODE_ID,
} from 'domains/catalog/Catalog.types';
import { ReferencePriceType } from 'domains/references';

export function getCatalogSourceUrl(source: CatalogSource | typeof UNIVERSAL_PRODUCTS): string {
  switch (source) {
    case 'DATAHUB':
      return ROUTER_CATALOG_DH;
    case 'IAM':
      return ROUTER_CATALOG_IAM;
    case 'UNIVERSAL_PRODUCTS':
      return ROUTER_UNIVERSAL_PRODUCTS;
    default:
      throw new Error(`Unknown catalog source: ${source}`);
  }
}

export function useCatalogSourceLocation(): CatalogSource | typeof UNIVERSAL_PRODUCTS {
  const path = useLocation().pathname;
  if (path.includes(ROUTER_CATALOG_DH)) {
    return 'DATAHUB';
  }
  if (path.includes(ROUTER_CATALOG_IAM)) {
    return 'IAM';
  }
  if (path.includes(ROUTER_UNIVERSAL_PRODUCTS)) {
    return 'UNIVERSAL_PRODUCTS';
  }
  throw new Error('Catalog source in url must exist');
}

export function useIsDHCatalogSourceInUrl(): boolean {
  const path = useLocation().pathname;
  return path.includes(ROUTER_CATALOG_DH);
}

export function useIsSearchInUrl(): boolean {
  const path = useLocation().pathname;
  return path.includes(ROUTER_CATALOG_SEARCH);
}

export function useIsCatalog(): boolean {
  const path = useLocation().pathname;
  return path.startsWith(CATALOG);
}

export function useGetCatalogSourceUrlFromLocation(): string {
  return getCatalogSourceUrl(useCatalogSourceLocation());
}

export function getDHCatalogSourcePaths(suffix: string): string[] {
  return [`${ROUTER_CATALOG_DH}${suffix}`];
}

export function getIAMCatalogSourcePaths(suffix: string): string[] {
  return [`${ROUTER_CATALOG_IAM}${suffix}`];
}

export function getUniversalProductsCatalogLevel2sourcePath(): string {
  return `${ROUTER_UNIVERSAL_PRODUCTS}/${UNIVERSAL_PRODUCTS_NODE_ID}/${UNIVERSAL_PRODUCTS_OILS_NODE_ID}`;
}

export function getUniversalProductsCatalogLevel3NodeId(
  level2tree: UniversalProductsCategory | undefined,
  value: string,
): string | undefined {
  return level2tree?.childCategories?.find((cat) => cat.name === value)?.nodeId;
}

export function getOilViscosityNodeName(name: string | undefined): string {
  const match = name ? CERTAS_OIL_VISCOSITY_REGEX.exec(name) : null;
  return match ? `${match[1]}${match[2]}` : '';
}

export function extractViscosityValues(value: string | undefined | null): string[] | null {
  if (!value) {
    return null;
  }
  return value.includes('|') ? value.split('|').map((v) => v.trim()) : [value];
}

export function isDH(source: CatalogSource | typeof UNIVERSAL_PRODUCTS): boolean {
  return source === 'DATAHUB';
}

export function isIAM(source: CatalogSource | typeof UNIVERSAL_PRODUCTS): boolean {
  return source === 'IAM';
}

type VehicleFilteredIamSearchStatus = Pick<SearchStatus, 'source' | 'type' | 'iamSearchScope' | 'vehicleKey'>;

export const isVehicleFilteredIamSearchResult = (
  searchStatus: VehicleFilteredIamSearchStatus | undefined,
  vehicleKey: string | undefined,
): boolean =>
  Boolean(
    vehicleKey &&
      searchStatus?.source === IAM &&
      searchStatus.type === 'FULLTEXT' &&
      searchStatus.iamSearchScope === 'VEHICLE_FILTERED' &&
      searchStatus.vehicleKey === vehicleKey,
  );

export const mapIamOEMPrice = (
  price: string | undefined,
  currency: string | undefined,
): ReferencePriceType | undefined => {
  if (!price || !currency || isNaN(Number(price))) {
    return undefined;
  }
  const priceInEuro = String(Number(price) / 100);
  return {
    currency,
    garageView: {
      vatExcludedPrice: priceInEuro,
      vatIncludedPrice: priceInEuro,
    },
    clientView: {
      recommendedPriceVatExcluded: priceInEuro,
      recommendedPriceVatIncluded: priceInEuro,
    },
  };
};

const catalogTypes = [OTHER_BRAND_KEY, OEM_BRAND_KEY] as const;

export const isValidIamCatalogType = (catalogType: string | null): catalogType is IamCatalogBrandsType =>
  catalogType !== null && (catalogTypes as readonly string[]).includes(catalogType);

export function formatCategoryLabelToDataCy(label: string, prefix?: string): string {
  const prefixString = prefix ? `${prefix}-` : '';
  return `${prefixString}${label
    .toLowerCase()
    .replaceAll('-', '')
    .replaceAll(/\s{1,2}/g, '-')}`;
}

export const GEARBOX_TYPE = 'gearbox-type';

export function reformatToLowerKebabCase(input: string): string {
  return input.toLowerCase().replace(/\s+/g, '-');
}

export function normalizeUniversalProductsSearchQuery(searchQuery?: string): string {
  return searchQuery?.trim() ?? '';
}

export function isSameUniversalProductsSearchQuery(left?: string, right?: string): boolean {
  return normalizeUniversalProductsSearchQuery(left) === normalizeUniversalProductsSearchQuery(right);
}
